/**
 * Thesis Change Detection Utilities
 *
 * Functions for detecting and formatting changes between thesis versions.
 * Used by the ThesisChangelog component to display version history.
 */

import type { PositionThesis } from '../services/api/positionThesisApi';
export { formatVersionDate } from './dateFormatting';
export { truncateText as truncateThesisText } from './textTruncation';

export interface ChangeSummary {
  field: 'conviction' | 'duration' | 'stop_loss' | 'take_profit' | 'thesis_text';
  oldValue: string | number | null;
  newValue: string | number | null;
}

export interface FormattedChange {
  text: string;
  type: 'conviction' | 'duration' | 'price' | 'text';
}

/**
 * Duration label mappings for translation
 */
const DURATION_LABELS: Record<string, string> = {
  Core: 'Core',
  Growth: 'Growth',
  Speculative: 'Speculative',
};

/**
 * Compare two thesis versions and return change summary.
 *
 * @param olderVersion - The older version (what changed FROM)
 * @param newerVersion - The newer version (what changed TO)
 * @param t - i18n translation function
 * @returns Array of formatted change strings, or null if no changes
 */
export function detectThesisChanges(
  olderVersion: PositionThesis,
  newerVersion: PositionThesis,
  t?: any
): FormattedChange[] | null {
  const changes: FormattedChange[] = [];

  // Check conviction level
  if (olderVersion.conviction_level !== newerVersion.conviction_level) {
    const oldVal = olderVersion.conviction_level ?? 0;
    const newVal = newerVersion.conviction_level ?? 0;
    const text = t
      ? t('portfolio.convictionChanged', {
          defaultValue: 'Conviction: {{old}} → {{new}} stars',
          old: oldVal,
          new: newVal,
        })
      : `Conviction: ${oldVal} → ${newVal} stars`;
    changes.push({ text, type: 'conviction' });
  }

  // Check duration
  if (olderVersion.duration !== newerVersion.duration) {
    const oldVal = olderVersion.duration ?? '—';
    const newVal = newerVersion.duration ?? '—';
    const text = t
      ? t('portfolio.durationChanged', {
          defaultValue: '{{old}} → {{new}}',
          old: formatDurationLabel(oldVal, t),
          new: formatDurationLabel(newVal, t),
        })
      : `${formatDurationLabel(oldVal)} → ${formatDurationLabel(newVal)}`;
    changes.push({ text, type: 'duration' });
  }

  // Check stop loss price
  if (olderVersion.stop_loss_price !== newerVersion.stop_loss_price) {
    const oldVal = olderVersion.stop_loss_price ?? '—';
    const newVal = newerVersion.stop_loss_price ?? '—';
    changes.push({
      text: formatPriceChange('Stop Loss', oldVal, newVal, '', t),
      type: 'price',
    });
  }

  // Check take profit price
  if (olderVersion.take_profit_price !== newerVersion.take_profit_price) {
    const oldVal = olderVersion.take_profit_price ?? '—';
    const newVal = newerVersion.take_profit_price ?? '—';
    changes.push({
      text: formatPriceChange('Take Profit', oldVal, newVal, '', t),
      type: 'price',
    });
  }

  // Check thesis text
  const oldText = olderVersion.thesis_text || '';
  const newText = newerVersion.thesis_text || '';
  const oldHasText = oldText.trim().length > 0;
  const newHasText = newText.trim().length > 0;

  if (oldHasText !== newHasText) {
    // Text was added or removed
    const text = t
      ? newHasText
        ? t('portfolio.thesisTextAdded', { defaultValue: 'Thesis added' })
        : t('portfolio.thesisTextRemoved', { defaultValue: 'Thesis removed' })
      : newHasText
        ? 'Thesis added'
        : 'Thesis removed';
    changes.push({ text, type: 'text' });
  } else if (oldHasText && newHasText && oldText.trim() !== newText.trim()) {
    // Text was modified
    const text = t
      ? t('portfolio.thesisTextChanged', { defaultValue: 'Thesis updated' })
      : 'Thesis updated';
    changes.push({ text, type: 'text' });
  }

  return changes.length > 0 ? changes : null;
}

/**
 * Format conviction level change for display.
 */
export function formatConvictionChange(
  oldLevel: number | null,
  newLevel: number | null,
  t?: any
): string | null {
  if (oldLevel === newLevel) return null;
  const oldVal = oldLevel ?? 0;
  const newVal = newLevel ?? 0;
  return t
    ? t('portfolio.convictionChanged', {
        defaultValue: 'Conviction: {{old}} → {{new}} stars',
        old: oldVal,
        new: newVal,
      })
    : `Conviction: ${oldVal} → ${newVal} stars`;
}

/**
 * Format duration change for display.
 */
export function formatDurationChange(
  oldDuration: string | null,
  newDuration: string | null,
  t?: any
): string | null {
  if (oldDuration === newDuration) return null;
  const oldVal = formatDurationLabel(oldDuration ?? '—', t);
  const newVal = formatDurationLabel(newDuration ?? '—', t);
  return t
    ? t('portfolio.durationChanged', {
        defaultValue: '{{old}} → {{new}}',
        old: oldVal,
        new: newVal,
      })
    : `${oldVal} → ${newVal}`;
}

/**
 * Format price change for display with translation support.
 */
export function formatPriceChange(
  field: 'Stop Loss' | 'Take Profit',
  oldValue: number | string | null,
  newValue: number | string | null,
  currency: string = '',
  t?: any
): string {
  // Map field names to translation keys
  const fieldKey = field === 'Stop Loss' ? 'stopLoss' : 'takeProfit';

  const oldStr =
    oldValue === null || oldValue === '—' ? '—' : `${currency}${Number(oldValue).toFixed(2)}`;
  const newStr =
    newValue === null || newValue === '—' ? '—' : `${currency}${Number(newValue).toFixed(2)}`;

  const label = t ? t(`portfolio.${fieldKey}`, { defaultValue: field }) : field;

  return `${label}: ${oldStr} → ${newStr}`;
}

/**
 * Format duration label with translation.
 */
const DURATION_I18N_KEYS: Record<string, string> = {
  Core: 'portfolio.durationCore',
  Growth: 'portfolio.durationGrowth',
  Speculative: 'portfolio.durationSpeculative',
};

function formatDurationLabel(duration: string | null | undefined, t?: any): string {
  if (!duration || duration === '—') return '—';

  if (t) {
    const key = DURATION_I18N_KEYS[duration] || `portfolio.duration${duration.replace('-', '')}`;
    return t(key, { defaultValue: DURATION_LABELS[duration] || duration });
  }

  return DURATION_LABELS[duration] || duration;
}

/**
 * Check if thesis text changed.
 */
export function hasThesisTextChanged(oldText: string | null, newText: string | null): boolean {
  const oldTrimmed = (oldText || '').trim();
  const newTrimmed = (newText || '').trim();
  return oldTrimmed !== newTrimmed;
}
